import { forwardRef, HTMLAttributes, useEffect, useRef, useState } from 'react';
import {
  Stack,
  Box,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Typography,
} from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { SubtaskItem as SubtaskItemType } from '@api';
import ImageInput from '../imageInput/ImageInput';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';

export type SubtaskItemProps = HTMLAttributes<HTMLDivElement> & {
  item: SubtaskItemType;
  invisible?: boolean;
  isDragging?: boolean;
  onUpdate?: (newVal: SubtaskItemType) => void;
  onDelete?: (id: number) => void;
  setFormValid?: (value: boolean | string) => void;
};

const SubtaskItem = forwardRef<HTMLDivElement, SubtaskItemProps>(
  ({ item, invisible, isDragging, onDelete, onUpdate, setFormValid, style, ...props }, ref) => {
    const active = useRef(false);
    const { attributes, listeners } = useSortable({ id: item.subtaskId });
    const [fileError, setFileError] = useState<null | string>(null);
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const subTaskTextRef = useRef<HTMLInputElement>(null);
    const [aiDesc, setAiDesc] = useState<string | undefined>(undefined);

    useEffect(() => {
      if (invisible && active.current) {
        active.current = false;
      }
    }, [invisible]);

    const handleFileChange = (file: File) => {
      setFileError(null);
      if (file) {
        if (file.type && file.type.startsWith('image/')) {
          if (item.picture?.startsWith('blob:')) URL.revokeObjectURL(item.picture);
          onUpdate?.({
            ...{
              ...item,
              subtaskText: subTaskTextRef.current?.value || item.subtaskText,
            },
            picture: URL.createObjectURL(file),
            pictureFile: file,
            pictureEdited: true,
          });
        } else {
          setFileError(t('Only images are allowed'));
        }
      }
    };

    const onDeleteItem = () => {
      if (item.picture?.startsWith('blob:')) URL.revokeObjectURL(item.picture);
      onDelete?.(item.subtaskId);
      setOpen(false);
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate?.({ ...item, subtaskText: e.target.value });
      setAiDesc(e.target.value);
    };

    return (
      <>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          ref={ref}
          sx={{
            py: 1,
            width: `100%`,
            opacity: invisible ? '0' : '1',
            borderRadius: '5px',
            cursor: isDragging ? 'grabbing' : 'grab',
            transform: isDragging ? 'rotate(5deg)' : 'rotate(0deg)',
            transformOrigin: 'left',
            boxShadow: isDragging ? 10 : 2,
            overflow: 'hidden',
            background: (theme) => theme.palette.background.paper,
            my: 0.4,
            ...style,
          }}
          {...props}
        >
          <Box
            sx={{
              height: '3em',
              width: '4em',
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 0,
              marginLeft: '-1em',
            }}
            {...attributes}
            {...listeners}
            aria-label={t('Drag to reorder') as string}
          >
            <DragIndicatorIcon />
          </Box>
          <ImageInput
            value={item.picture}
            onChange={handleFileChange}
            imageLoading={(loading: boolean) => {
              if (loading) {
                setFormValid?.(false);
              } else {
                setFormValid?.(true);
              }
            }}
            sx={{ width: '5em', height: '5em', flexGrow: 0, aspectRatio: '1/1' }}
            aiDesc={item.subtaskText}
            isSubtask
          />
          <TextField
            inputRef={subTaskTextRef}
            type="text"
            sx={{
              ml: 2,
              flexGrow: 1,
            }}
            onChange={handleTextChange}
            value={item.subtaskText}
            placeholder={t('Add text here') ?? ''}
            InputProps={{
              sx: {
                borderRadius: '1em',
              },
            }}
            inputProps={{
              maxLength: 64, //should match db
            }}
            autoComplete="off"
            name="subtaskText"
          />
          <IconButton
            color="error"
            sx={{ flexGrow: 0 }}
            aria-label="Remove item"
            onClick={() => setOpen(true)}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t('Are you sure you want to delete this item?')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={() => onDeleteItem()} autoFocus color="error">
              {t('Remove')}
            </Button>
          </DialogActions>
        </Dialog>
        <Typography variant="caption" sx={{ color: 'red' }}>
          {fileError}
        </Typography>
      </>
    );
  },
);

export default SubtaskItem;
